import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import fr from './locales/fr-core.generated.json';
import { ROUTE_NAMESPACES, PARTIAL_NAMESPACES } from './locales/route-namespaces.generated';

// French is the reference language (fallbackLng) and the language of the
// <title>/meta rendered server-side, so its CORE is inlined: the first render
// stays synchronous, with no translation fetch on the critical path.
//
// The core is generated by scripts/split-locales.mjs from the eager import
// graph of main.tsx, so it cannot miss a string the homepage renders. English
// and Arabic were already fetched on demand, and stay that way.
//
// The rest of the dictionary (audit, admin, dashboard, legal, ui_components...)
// was 33 KB gzipped inlined in the entry chunk: every visitor downloaded the
// strings of pages none of them opened. Each namespace is now a small file in
// /locales/<lang>/ns/, fetched when a route or a lazy home section needs it.
const resources = { fr: { translation: fr } } as const;

/** Namespaces already merged into i18next for `lang`, so we never refetch. */
const loaded = new Set<string>();
/** In-flight fetches, so N components asking at once produce one request. */
const pending = new Map<string, Promise<void>>();

const LANGUAGES = ['fr', 'en', 'ar'] as const;

/** Namespaces in the core but not complete: they still need their file fetched. */
const partial = new Set<string>(PARTIAL_NAMESPACES);

async function loadNamespaceFile(lang: string, ns: string): Promise<void> {
  const { loadNamespace } = await import('./locales/loadTranslation');
  const mod = await loadNamespace(lang, ns);
  const data = (mod as { default?: Record<string, unknown> }).default ?? (mod as Record<string, unknown>);
  i18n.addResourceBundle(lang, 'translation', data, true, true);
}

/**
 * Ensures `namespaces` are available in `lang`, fetching what is missing.
 * Resolves once i18next can translate every one of them, so callers may render
 * immediately afterwards without ever showing a raw key.
 */
export function ensureNamespaces(lng: string, namespaces: readonly string[]): Promise<void> {
  const lang = lng.split('-')[0];
  if (!LANGUAGES.includes(lang as (typeof LANGUAGES)[number])) return Promise.resolve();

  const jobs: Promise<void>[] = [];
  for (const ns of namespaces) {
    const token = `${lang}/${ns}`;
    // Already merged, or inlined in the core at init: nothing to do.
    if (loaded.has(token) || pending.has(token)) {
      if (pending.has(token)) jobs.push(pending.get(token)!);
      continue;
    }
    // A namespace already in the core needs no fetch, UNLESS it is only there
    // partially: `home_modern.hero` is inlined, `home_modern.services` is not,
    // and the test below would wrongly consider the namespace complete.
    const complete = i18n.getResource(lang, 'translation', ns) !== undefined && !partial.has(ns);
    if (complete && lang === 'fr') {
      loaded.add(token);
      continue;
    }
    const job = loadNamespaceFile(lang, ns)
      .then(() => {
        loaded.add(token);
      })
      .catch(async (e) => {
        // Missing file or network hiccup. Leaving the namespace unloaded lets
        // i18n fall back to French rather than showing a raw key — but if the
        // miss was transient (deploy in flight, network blip) the route would
        // keep rendering raw keys for the whole session, since nothing
        // re-triggers `Namespaces`. One delayed retry heals that case; if the
        // file is truly gone we behave exactly as before.
        console.warn('[i18n] could not load namespace', token, e);
        await new Promise((r) => setTimeout(r, 1500));
        try {
          await loadNamespaceFile(lang, ns);
          loaded.add(token);
        } catch (e2) {
          console.warn('[i18n] retry failed, rendering with fallbacks', token, e2);
        }
      })
      .finally(() => pending.delete(token));
    pending.set(token, job);
    jobs.push(job);
  }
  return jobs.length ? Promise.all(jobs).then(() => undefined) : Promise.resolve();
}

// Whole-dictionary load, kept for the language switcher: switching to English
// must not leave half the site in French, so the complete set is fetched at
// once and cached for the session.
export const ensureLanguage = async (lng: string) => {
  const lang = lng.split('-')[0];
  if (lang === 'fr') return;
  if (i18n.hasResourceBundle(lang, 'translation')) return;
  try {
    const { loadLanguage } = await import('./locales/loadTranslation');
    const mod = await loadLanguage(lang);
    const data = (mod as { default?: Record<string, unknown> }).default ?? (mod as Record<string, unknown>);
    i18n.addResourceBundle(lang, 'translation', data, true, true);
  } catch (e) {
    console.warn('[i18n] fallback to fr for', lang, e);
  }
};

/** Namespaces declared by a page, per the generated map. */
export const namespacesFor = (page: string): readonly string[] =>
  ROUTE_NAMESPACES[page] ?? [];

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Local core resources make the first render independent of any fetch.
    resources,
    initAsync: false,
    fallbackLng: 'fr',
    load: 'languageOnly',
    supportedLngs: ['fr', 'en', 'ar'],
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['querystring'],
      lookupQuerystring: 'lang',
      caches: [],
    },
    react: {
      useSuspense: true,
    },
  });

// If the visitor arrived on ?lang=en or ?lang=ar, pull that dictionary in now
// and swap once it resolves. The site paints in French first in the meantime,
// which is far better than holding the whole bundle back until a 30 KB fetch
// finishes.
// Guarded because this module is also imported in Node (test runner, server-side
// SEO rendering), where there is no `window`.
const requested =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('lang')
    : null;
if (requested && ['en', 'ar'].includes(requested.split('-')[0])) {
  void ensureLanguage(requested).then(() => i18n.changeLanguage(requested));
}

export default i18n;
